import React from "react";
import { ArchiConversation } from "./types";

interface Props {
  conversations: ArchiConversation[];
  activeId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  onDelete: (id: number) => void;
  /**
   * In compact mode (sidebar OPEN, narrower viewport) the list takes
   * less width and hides the timestamp line. In expanded it shows the
   * full layout. Either way the history is always visible — never gate
   * the list behind the expand button.
   */
  compact?: boolean;
}

const formatRelative = (iso: string): string => {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
};

const ArchiConversationList: React.FC<Props> = ({
  conversations,
  activeId,
  onSelect,
  onNew,
  onDelete,
  compact = false,
}) => {
  const widthClass = compact ? "w-52" : "w-72";

  return (
    <div
      className={`
        shrink-0 ${widthClass}
        h-full
        border-r border-semantic-border-secondary
        bg-semantic-bg-secondary
        flex flex-col
      `}
    >
      <div className="p-2 border-b border-semantic-border-secondary">
        <button
          type="button"
          onClick={onNew}
          className="
            w-full
            text-sm
            px-3 py-1.5
            rounded
            bg-semantic-bg-primary
            border border-semantic-border-secondary
            text-semantic-text-primary
            hover:bg-semantic-state-active
            transition-colors
          "
        >
          + New chat
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <p className="px-3 py-4 text-xs text-semantic-text-secondary">
            No previous chats yet.
          </p>
        ) : (
          conversations.map((c) => {
            const active = c.id === activeId;
            return (
              <div
                key={c.id}
                className={`
                  group
                  flex items-center justify-between gap-1
                  px-2 py-2
                  border-b border-semantic-border-secondary
                  cursor-pointer
                  ${active ? "bg-semantic-state-active" : "hover:bg-semantic-state-active"}
                `}
                onClick={() => onSelect(c.id)}
                title={c.title || "Untitled"}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-semantic-text-primary truncate">
                    {c.title || "Untitled"}
                  </div>
                  {!compact && (
                    <div className="text-[10px] text-semantic-text-secondary mt-0.5">
                      {formatRelative(c.updatedAt)}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(c.id);
                  }}
                  className="
                    shrink-0
                    opacity-0 group-hover:opacity-100
                    p-1 rounded
                    text-semantic-text-secondary
                    hover:text-semantic-text-danger
                    hover:bg-semantic-bg-danger-secondary
                    transition-opacity
                  "
                  aria-label="Delete chat"
                  title="Delete chat"
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M3 4h10M6 4V2.5A.5.5 0 016.5 2h3a.5.5 0 01.5.5V4M5 4l.5 9a1 1 0 001 1h3a1 1 0 001-1L11 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ArchiConversationList;
